import React, { useState } from 'react';

import { TextLink, Dialog } from '@oneaudi/unified-web-components';

import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';

import { useI18n } from '@oneaudi/i18n-context';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';

import {
  ConsumptionTileElementPLP,
  DynamicWrapper,
  EnvKConsumptionAndEmissions,
  FootnoteReferenceContextProvider,
  FootnotesReferenceText,
  getRelatedProduct,
  handleFootnoteLinkClick,
  useServicesContext,
  VehicleBasic,
} from '@oneaudi/vtp-shared';

import { trackLayerClose, trackLayerLinkClick, trackLayerOpen } from '../../tracking/tilesTracking';
import { ConsumptionEmissionLayerPlp } from './ConsumptionAndEmissionLayer/ConsumptionEmissionLayerPlp';

import {
  AlphaOnlyWrapper,
  ConsumptionEmissionContainer,
  LinkButtonContainer,
} from './ActionButtons.styles';

interface Props {
  vehicle: VehicleBasic;
}

const ConsumptionAndEmission: React.FC<Props> = ({ vehicle }) => {
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;

  const footnoteReferenceServiceScopeManager = useServicesContext().getAdditionalService(
    'audi-footnote-reference-service'
  ) as AudiFootnoteRefernceServiceScopeManagerInterfaceV3;

  const [isConsumptionDialogOpen, setIsConsumptionDialogOpen] = useState<boolean>(false);

  const consumptionLayerLabel = useI18n({ id: 'nemo.ui.sc.tiles.consumption.link.text' });
  const consumptionLinkAriaLabel = useI18n({
    id: 'nemo.ui.sc.tiles.consumption.link.text.arialabel',
  });
  const closeBtnAriaLabel = useI18n({ id: 'nemo.ui.sc.tiles.consumption.dialog.close' });
  const dialogAriaLabel = useI18n({ id: 'nemo.ui.sc.tiles.consumption.dialog' });

  const isDEMarket = localeService.countryCode?.toLowerCase() === 'de';

  const relatedProduct = getRelatedProduct(vehicle);

  const hasConsumptionValues = (veh: VehicleBasic | undefined) => {
    return (
      veh?.io?.hasWltp ||
      veh?.io?.hasNedc ||
      veh?.vlsEnergyProvision?.hasWltp ||
      veh?.vlsEnergyProvision?.hasNedc
    );
  };

  return (
    <DynamicWrapper AlphaWrapperFc={AlphaOnlyWrapper}>
      {isDEMarket && (
        <div>
          <EnvKConsumptionAndEmissions vehicle={vehicle} data-testid="pkv-envkv" />
        </div>
      )}
      {!isDEMarket && hasConsumptionValues(vehicle) && (
        <ConsumptionEmissionContainer>
          <ConsumptionTileElementPLP
            vehicleId={vehicle.id}
            order={['consumption', 'emission']}
            displayedItems={['emission', 'consumption']}
            hideLayerBtn
            isPlp
            data-testid="consumptionElement"
          />

          <LinkButtonContainer>
            <TextLink
              aria-label={consumptionLinkAriaLabel}
              variant="standalone"
              onClick={() => {
                trackLayerLinkClick(
                  trackingService,
                  consumptionLayerLabel,
                  'text link',
                  relatedProduct
                );
                trackLayerOpen(trackingService, 'emission layer', 'vtp emission layer', undefined);
                setIsConsumptionDialogOpen(true);
              }}
              size="md"
              data-testid="consumptionLayer"
            >
              {consumptionLayerLabel}
            </TextLink>
          </LinkButtonContainer>
        </ConsumptionEmissionContainer>
      )}
      {isConsumptionDialogOpen && footnoteReferenceServiceScopeManager && (
        <Dialog
          aria-label={dialogAriaLabel}
          onClose={() => {
            if (trackingService) {
              trackLayerClose(trackingService, 'vtp emission layer');
            }
            setIsConsumptionDialogOpen(false);
          }}
          open={isConsumptionDialogOpen}
          onCloseButtonAriaLabel={closeBtnAriaLabel}
          variant="focus-small"
        >
          <div className="ConsumptionLayer" onClickCapture={handleFootnoteLinkClick}>
            <FootnoteReferenceContextProvider
              footnoteReferenceServiceScopeManager={footnoteReferenceServiceScopeManager}
              customScope="priceBreakDownLayer"
            >
              <ConsumptionEmissionLayerPlp vehicle={vehicle} />
              <FootnotesReferenceText />
            </FootnoteReferenceContextProvider>
          </div>
        </Dialog>
      )}
    </DynamicWrapper>
  );
};

export default ConsumptionAndEmission;
